import React, { useState, useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useHistory } from "react-router-dom";
import notify from "devextreme/ui/notify";
import DateBox from "devextreme-react/date-box";
import Icon from "./Icon";
import "../styles/hogery-theme.css";
import {
  validateIsraeliId,
  validatePhoneDuplicates,
  validateFileSize,
  validateEmails,
} from "../validators/validators.js";
import DataSetManager from "../../../utils/dataSetManager.js";
import FilterEngine from "../../../utils/filterEngine.js";
import { COUNTRY_CODES, DEFAULT_COUNTRY_ISO } from "../data/countryCodes.js";
import { normalizePhone, parsePhone } from "../utils/phone.js";
import { getParameterFromUrl } from "../utils/urlUtils.js";
import { logDraftPromptEvent } from "../services/formApiService.js";

const { REACT_APP_SERVER_URL } = process.env;

/** Approximate height of the country menu, used to decide whether to flip it above the field. */
const MENU_MAX_H = 300;
const MENU_MIN_H = 180;

// Find the element that ACTUALLY scrolls the form — the DevExtreme ScrollView's
// native container (or whichever ancestor is the real scroll container). We scroll
// ONLY this element, never scrollIntoView(): scrollIntoView scrolls EVERY scrollable
// ancestor, which tucks the sticky App Header (.header-component) and the sticky
// .tabs-header ("second header" with the save-draft buttons) out of view. Scrolling
// just the inner container keeps both headers pinned and visible.
function getScrollableAncestor(fromEl) {
  // Prefer the DevExtreme ScrollView's native container — the app's real scroller
  // (the layout scrolls it via scrollViewRef.instance.scrollTo, see
  // side-nav-outer-toolbar.js). Fall back to the nearest scrollable ancestor.
  const dx = fromEl?.closest(".dx-scrollable-container");
  if (dx && dx.scrollHeight > dx.clientHeight + 1) return dx;
  let node = fromEl?.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    const oy = window.getComputedStyle(node).overflowY;
    if ((oy === "auto" || oy === "scroll") && node.scrollHeight > node.clientHeight + 1) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

// Scroll a target element into view within the form's real scroll container,
// landing it just below the sticky .tabs-header — WITHOUT scrollIntoView, which
// scrolls EVERY ancestor and tucks the sticky App Header / .tabs-header out of
// view. Use this for ANY "scroll to a field / banner" inside the form.
function scrollElementIntoForm(el) {
  if (!el) return;
  const sc = getScrollableAncestor(el);
  const headerH = document.querySelector(".tabs-header")?.offsetHeight ?? 0;
  if (sc) {
    const delta = el.getBoundingClientRect().top - sc.getBoundingClientRect().top - headerH - 16;
    sc.scrollBy({ top: delta, behavior: "smooth" });
  } else {
    el.style.scrollMarginTop = headerH + 16 + "px";
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

export default function HogeryForm({
  schema,
  docRef,
  templateRef,
  evaluateVisibility,
  execFunction,
  updatePhones,
  state,
  updateState,
  onSendForm,
  onSaveFile,
  onSaveChanges,
  autoSaveDraft,
  formElementRef,
  setMessagePopupVisibility,
  deleteOwner,
  handleUploadFile,
  saveAndReset,
  deleteAndReset,
  executeFunctionByUid,
  validateConnection,
}) {
  const history = useHistory();
  const [step, setStep] = useState(0);
  const [instanceIndices, setInstanceIndices] = useState({});
  const [touched, setTouched] = useState({});
  const [allTouched, setAllTouched] = useState(false);
  const [saveDropdownOpen, setSaveDropdownOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState(null);
  const [refreshPopupVisible, setRefreshPopupVisible] = useState(false);
  const [templateNamePopupVisible, setTemplateNamePopupVisible] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem("form_theme") || "light");
  const [validationErrors, setValidationErrors] = useState([]);
  // Auto-save opt-in state: a draft is "established" once the user saved one (URL has ?t=)
  const [draftEstablished, setDraftEstablished] = useState(() => !!getParameterFromUrl("t"));
  const [autoSavePromptVisible, setAutoSavePromptVisible] = useState(false);
  const [autoSavePromptDismissed, setAutoSavePromptDismissed] = useState(false);
  // Confirm-button state: locks the button while the save runs, and surfaces a failure
  // inside the dialog (there is no toast mechanism in this app).
  const [autoSaveSubmitting, setAutoSaveSubmitting] = useState(false);
  const [autoSaveError, setAutoSaveError] = useState("");
  const autoSaveModalRef = useRef(null);
  const autoSaveConfirmRef = useRef(null);
  // "Don't show again" — browser-local, same convention as form_autosave / form_theme.
  const [autoSavePromptMuted, setAutoSavePromptMuted] = useState(() => {
    try { return localStorage.getItem("form_autosave_prompt") === "off"; } catch (e) { return false; }
  });
  const [autoSavePromptDontAsk, setAutoSavePromptDontAsk] = useState(false);
  const [muteConfirmVisible, setMuteConfirmVisible] = useState(false);
  const [saveHintVisible, setSaveHintVisible] = useState(false);
  const hintDirtyBaselineRef = useRef(0);

  useEffect(() => {
    localStorage.setItem("form_theme", theme);
    document.documentElement.setAttribute("data-theme", theme);
    // background ownership belongs to TestForm.jsx (mounts once, cleans up on unmount)
    // HogeryForm must not reach outside its own DOM tree
  }, [theme]);

  const toggleTheme = () => setTheme(t => t === "dark" ? "light" : "dark");

  // ---- AUTO-SAVE (silent draft saving) ----
  const autoSaveTimerRef = useRef(null);
  const lastSavedDirtyRef = useRef(0);
  const autoSaveInFlightRef = useRef(false);

  // A draft exists once the user has saved one (URL carries ?t=) — auto-save only
  // ever UPDATES that draft; it never creates one on its own.
  const hasDraft = draftEstablished || !!getParameterFromUrl("t");

  const runAutoSave = useCallback(async () => {
    if (!autoSaveDraft || autoSaveInFlightRef.current) return;
    const dirty = state._dirtyCount || 0;
    if (dirty === lastSavedDirtyRef.current) return;
    autoSaveInFlightRef.current = true;
    try {
      const ok = await autoSaveDraft();
      if (ok) lastSavedDirtyRef.current = dirty;
    } finally {
      autoSaveInFlightRef.current = false;
    }
  }, [autoSaveDraft, state._dirtyCount]);

  // Offer to save a draft as soon as the form has loaded — before any input, so the
  // choice is made once on entry rather than interrupting mid-fill. New forms only:
  // a task id in the URL means the user already has a draft, and templates are never
  // auto-saved (useFormActions guards that too).
  useEffect(() => {
    if (state.isLoading) return;
    if (hasDraft) return;
    if (state.sourceStatus === "תבנית") return;
    if (autoSavePromptDismissed) return;
    if (!state.autoSaveEnabled) return;  // turned auto-save off — don't pitch it again
    if (autoSavePromptMuted) return;     // asked us to stop showing this
    setAutoSavePromptVisible(true);
  }, [
    state.isLoading, state.sourceStatus, state.autoSaveEnabled,
    hasDraft, autoSavePromptDismissed, autoSavePromptMuted,
  ]);

  useEffect(() => {
    if (autoSaveTimerRef.current) { clearTimeout(autoSaveTimerRef.current); autoSaveTimerRef.current = null; }
    if (state.isLoading) return;
    const pending = (state._dirtyCount || 0) - lastSavedDirtyRef.current;
    if (pending <= 0) return;
    if (!hasDraft) return;
    if (!state.autoSaveEnabled) return;

    // Save immediately once enough changes accumulate, otherwise debounce ~45s
    // after the last change (inactivity).
    if (pending >= 5) { runAutoSave(); return; }
    autoSaveTimerRef.current = setTimeout(runAutoSave, 45000);
    return () => {
      if (autoSaveTimerRef.current) { clearTimeout(autoSaveTimerRef.current); autoSaveTimerRef.current = null; }
    };
  }, [state._dirtyCount, state.autoSaveEnabled, state.isLoading, hasDraft, runAutoSave]);

  const confirmAutoSavePrompt = useCallback(async () => {
    if (!autoSaveDraft || autoSaveInFlightRef.current) return;
    autoSaveInFlightRef.current = true;
    // Mark as answered BEFORE awaiting: until the save returns, hasDraft is still false,
    // so any further typing would re-trigger the effect and re-open this dialog.
    setAutoSavePromptDismissed(true);
    setAutoSaveError("");
    setAutoSaveSubmitting(true);
    try {
      const ok = await autoSaveDraft({ allowCreate: true });
      if (ok) {
        setDraftEstablished(true);
        lastSavedDirtyRef.current = state._dirtyCount || 0;
        if (!state.autoSaveEnabled) {
          try { localStorage.setItem("form_autosave", "on"); } catch (e) { /* ignore */ }
          updateState({ autoSaveEnabled: true });
        }
        setAutoSavePromptVisible(false);
        logDraftPromptEvent("Used");  // only a save that actually succeeded counts
      } else {
        // Keep the dialog open so the failure is visible and retryable, and allow the
        // prompt to fire again later since no draft was created.
        setAutoSavePromptDismissed(false);
        setAutoSaveError("שמירת הטיוטה נכשלה. בדוק את החיבור ונסה שוב.");
      }
    } finally {
      setAutoSaveSubmitting(false);
      autoSaveInFlightRef.current = false;
    }
  }, [autoSaveDraft, state._dirtyCount, state.autoSaveEnabled, updateState]);

  const dismissAutoSavePrompt = useCallback(() => {
    if (autoSaveInFlightRef.current) return;
    setAutoSaveError("");
    setAutoSavePromptVisible(false);
    setAutoSavePromptDismissed(true);
    logDraftPromptEvent("Cancelled");
  }, []);

  // Muting silences a data-protection feature for good, so it takes a second, explicit
  // confirmation — a stray tick on the checkbox must not be enough.
  const cancelMute = useCallback(() => {
    // Cancels only the permanent muting — the offer itself is still answered, so it
    // closes for this session rather than popping back up and asking again.
    setMuteConfirmVisible(false);
    setAutoSavePromptDontAsk(false);
    setAutoSavePromptVisible(false);
    setAutoSavePromptDismissed(true);
  }, []);

  const confirmMute = useCallback(() => {
    try { localStorage.setItem("form_autosave_prompt", "off"); } catch (e) { /* ignore */ }
    setAutoSavePromptMuted(true);
    setAutoSavePromptVisible(false);  // close the offer sitting behind this dialog
    setAutoSavePromptDismissed(true);
    setMuteConfirmVisible(false);
    hintDirtyBaselineRef.current = state._dirtyCount || 0;
    setSaveHintVisible(true);
    logDraftPromptEvent("DismissedForever");  // the second confirmation, not the tick
  }, [state._dirtyCount]);

  // Close the hint as soon as the user types: it links to the profile, and navigating
  // away mid-entry would lose their input. Measured from the count at mute time, since
  // they may already have filled fields before answering the prompt.
  useEffect(() => {
    if (!saveHintVisible) return;
    if ((state._dirtyCount || 0) > hintDirtyBaselineRef.current) setSaveHintVisible(false);
  }, [state._dirtyCount, saveHintVisible]);

  // Make the prompt a real modal: move focus into it (otherwise typing keeps going to
  // the field behind it), keep Tab inside it, and close on Escape.
  useEffect(() => {
    if (!autoSavePromptVisible) return;
    autoSaveConfirmRef.current?.focus();
    const onKey = (e) => {
      // While the mute confirmation is up it owns Escape: dismissing the offer
      // underneath it would leave that dialog stranded and count a false decline.
      if (e.key === "Escape") { muteConfirmVisible ? cancelMute() : dismissAutoSavePrompt(); return; }
      if (e.key !== "Tab") return;
      const items = autoSaveModalRef.current?.querySelectorAll(
        'button:not([disabled]), input, select, textarea, [href], [tabindex]:not([tabindex="-1"])'
      );
      if (!items || items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [autoSavePromptVisible, dismissAutoSavePrompt, muteConfirmVisible, cancelMute]);

  const toggleAutoSave = useCallback(() => {
    const next = !state.autoSaveEnabled;
    try { localStorage.setItem("form_autosave", next ? "on" : "off"); } catch (e) { /* ignore */ }
    updateState({ autoSaveEnabled: next });
  }, [state.autoSaveEnabled, updateState]);

  const formatSavedTime = (ts) => {
    try {
      return new Date(ts).toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" });
    } catch (e) {
      return "";
    }
  };

  // ---- VALIDATION ----

  const VALIDATABLE_TYPES = new Set([
    "text","id","email","phone","number","date","textarea",
    "select","radio","dropdown_db","multiselect","checkbox","file",
  ]);

  const validateFormField = useCallback(
    (field, value, tabId, isArrayTab, curIdx) => {
      if (!VALIDATABLE_TYPES.has(field.type)) return null;

      const validation = field.validation || "";

      // Check required via requiredExpr or field.required
      const isRequired = field.requiredExpr
        ? evaluateVisibility(
            isArrayTab
              ? field.requiredExpr.replaceAll("[x]", "[" + curIdx + "]")
              : field.requiredExpr
          )
        : !!field.required;

      // Validation-type checks first
      if (validation.includes("id") && value && String(value).trim() !== "") {
        if (!validateIsraeliId(value)) return "יש להזין ת.ז תקין";
      }

      if (validation.includes("phone") && value && String(value).trim() !== "") {
        if (!validatePhoneDuplicates(value, state.phonesNumbers || [])) {
          return "אין לעשות שימוש חוזר במספר טלפון";
        }
      }

      if (validation.includes("email") && value && String(value).trim() !== "") {
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(value)) return "יש להזין אימייל תקין";
      }


      if (validation.includes("multipleEmails") && value && String(value).trim() !== "") {
        if (!validateEmails(value)) return field.validationMessage || "יש להזין אימיילים תקינים";
      }

      if (validation.includes("pattern") && value && String(value).trim() !== "") {
        if (field.validationPatameters) {
          const regex = new RegExp(field.validationPatameters);
          if (!regex.test(value)) return field.validationMessage || "ערך לא תקין";
        }
      }

      if ((validation.includes("numeric") || field.originalType === "dxNumberBox") && value !== "" && value !== null && value !== undefined) {
        const cleaned = String(value).replace(/,/g, "");
        if (cleaned !== "" && !/^-?\d+(\.\d+)?$/.test(cleaned)) return field.validationMessage || "יש להזין מספרים בלבד";
      }

      if (validation.includes("range") && value !== "" && value !== null && value !== undefined) {
        const num = parseFloat(value);
        const min = parseFloat(field.minValue);
        const max = parseFloat(field.maxValue);
        if (!isNaN(num) && ((!isNaN(min) && num < min) || (!isNaN(max) && num > max))) {
          return field.validationMessage || `יש להזין ערך בין ${min} ל-${max}`;
        }
      }

      if (validation.includes("stringLength") && value !== "" && value !== null && value !== undefined) {
        const len = String(value).length;
        const min = field.minValue !== "" && field.minValue !== null ? parseInt(field.minValue) : 0;
        const max = field.maxValue !== "" && field.maxValue !== null ? parseInt(field.maxValue) : 1000000;
        if (len < min || len > max) {
          return field.validationMessage || `אורך הטקסט חייב להיות בין ${min} ל-${max} תווים`;
        }
      }

      // img — file size validation (mirrors test-form.js ValidateImg + getValidation line 2407)
      if (validation.includes("img")) {
        const files = Array.isArray(value) ? value : (value instanceof File ? [value] : []);
        const skipRequired = docRef.current?.skipRequired;
        if (files.length > 0 && !validateFileSize(files, validation)) {
          const maxMB = validation.includes("|") ? validation.split("|")[1] : "4";
          return field.validationMessage || `הקובץ שהעלית חורג מהגודל המבוקש (מקסימום ${maxMB}MB)`;
        } else if (files.length === 0 && skipRequired) {
          // allow empty when skipRequired — same as TestForm line 2413
        }
      }

      // date range validation — mirrors test-form.js DateBox min/max (line 1260-1262)
      if (field.type === "date" && validation.includes("range") && value && value !== "") {
        const dateVal = new Date(value);
        const minDate = field.min ? new Date(field.min) : null;
        const maxDate = field.max ? new Date(field.max) : null;
        if ((minDate && dateVal < minDate) || (maxDate && dateVal > maxDate)) {
          return field.validationMessage || "תאריך מחוץ לטווח המותר";
        }
      }

      // Required check
      const skipRequired = docRef.current?.skipRequired;
      if (isRequired && !skipRequired) {
        // For file fields, must be actual File instance (not empty object {} from old drafts)
        // Supports both single file and multiple files (array)
        const isFileField = field.type === "file";
        const isValidFile = value instanceof File ||
          (Array.isArray(value) && value.length > 0 && value[0] instanceof File);
        const isEmpty =
          value === undefined ||
          value === null ||
          value === "" ||
          value === false ||
          (Array.isArray(value) && value.length === 0) ||
          (isFileField && !isValidFile);
        if (isEmpty) return "חובה למלא שדה " + field.caption;
      }

      return null;
    },
    [evaluateVisibility, state.phonesNumbers, docRef]
  );

  const validateAll = useCallback(() => {
    const tabs = schema.tabs.filter((t) => {
      if (!t.visible) return true;
      if (t.isArray) {
        const items = docRef.current[t.id] || [{}];
        const expr = t.visible.replaceAll("[x]", "[" + (items.length - 1) + "]");
        return evaluateVisibility(expr);
      }
      return evaluateVisibility(t.visible);
    });

    const errors = [];

    for (let tabIdx = 0; tabIdx < tabs.length; tabIdx++) {
      const tab = tabs[tabIdx];
      const tabId = tab.id;
      const isArrayTab = tab.isArray;
      const instances = isArrayTab ? (docRef.current[tabId] || [{}]) : [null];

      for (let idx = 0; idx < instances.length; idx++) {
        const curIdx = isArrayTab ? idx : 0;
        for (const section of tab.sections) {
          for (const field of section.fields) {
            // Visibility check
            let visExpr = field.visible;
            if (visExpr && isArrayTab) {
              visExpr = visExpr.replaceAll("[x]", "[" + curIdx + "]");
            }
            if (visExpr && !evaluateVisibility(visExpr)) continue;

            const value = isArrayTab
              ? docRef.current[tabId]?.[curIdx]?.[field.uid]
              : docRef.current[tabId]?.[field.uid];

            const error = validateFormField(field, value, tabId, isArrayTab, curIdx);
            if (error) {
              errors.push({ tabIndex: tabIdx, tabId, fieldUid: field.uid, instanceIdx: curIdx, message: error });
            }
          }
        }
      }
    }

    return { isValid: errors.length === 0, errors };
  }, [schema, docRef, evaluateVisibility, validateFormField]);

  // ---- FIELD CHANGE ----

  const handleFieldChange = useCallback(
    (tabId, isArrayTab, field, value, instanceIdx) => {
      if (isArrayTab) {
        if (!docRef.current[tabId]) docRef.current[tabId] = [{}];
        if (!docRef.current[tabId][instanceIdx]) docRef.current[tabId][instanceIdx] = {};
        docRef.current[tabId][instanceIdx][field.uid] = value;
      } else {
        if (!docRef.current[tabId] || typeof docRef.current[tabId] !== 'object') docRef.current[tabId] = {};
        docRef.current[tabId][field.uid] = value;
      }

      // Phone tracking
      if (field.type === "phone") {
        const prevValue = isArrayTab
          ? docRef.current[tabId]?.[instanceIdx]?.[field.uid]
          : docRef.current[tabId]?.[field.uid];
        const phones = [...(state.phonesNumbers || [])];
        // Remove previous value
        if (prevValue && prevValue !== value) {
          const prevIdx = phones.indexOf(prevValue);
          if (prevIdx !== -1) phones.splice(prevIdx, 1);
        }
        // Add new value if non-empty
        if (value && value.trim() !== "") {
          // Only add if not already present at the right position
          if (!phones.includes(value)) {
            phones.push(value);
          }
        }
        updateState({ phonesNumbers: phones });
      }

      if (field.onChangeValue) {
        const expr = field.onChangeValue.replaceAll("[x]", "[" + instanceIdx + "]");
        execFunction(expr, { value });
      }
      updatePhones();

      // Mark the form dirty so auto-save picks it up.
      updateState((s) => ({ _dirtyCount: (s._dirtyCount || 0) + 1 }));

      // Re-run full validation after each change so the banner reflects newly visible fields too
      if (allTouched) {
        const { errors } = validateAll();
        setValidationErrors(errors);
      }
    },
    [docRef, execFunction, updatePhones, state.phonesNumbers, updateState, validateAll, allTouched]
  );

  const handleBlur = useCallback(
    (fieldKey) => {
      setTouched((prev) => ({ ...prev, [fieldKey]: true }));
    },
    []
  );

  // ---- INSTANCE MANAGEMENT ----

  const addInstance = useCallback(
    (tabId, maxItems) => {
      if (!docRef.current[tabId]) docRef.current[tabId] = [{}];
      const items = docRef.current[tabId] || [];
      if (!maxItems || items.length < maxItems) {
        docRef.current[tabId].push({});
        updatePhones();
        updateState((s) => ({ _dirtyCount: (s._dirtyCount || 0) + 1 }));
      }
    },
    [docRef, updatePhones, updateState]
  );

  const removeInstance = useCallback(
    (tabId, tabTitle, idx) => {
      const items = docRef.current[tabId] || [];
      if (items.length <= 1) return;
      const fields = [{ object: tabId }];
      setMessagePopupVisibility(true);
      updateState({
        popupData: { fields, index: idx },
        popupTitle: "הסרת " + tabTitle,
        popupMessage: "האם להסיר " + tabTitle + "?",
      });
    },
    [docRef, setMessagePopupVisibility, updateState]
  );

  const setCurrentInstance = useCallback((tabId, idx) => {
    setInstanceIndices((prev) => ({ ...prev, [tabId]: idx }));
  }, []);

  const getInstanceBaseLabel = (tab) => {
    if (tab.instanceLabel) return tab.instanceLabel;
    const label = tab.arrayLabel || tab.title || "";
    return label.startsWith("הוסף ") ? label.slice(5) : label;
  };

  const getInstanceLabel = useCallback((tab, tabId, idx) => {
    const instance = docRef.current[tabId]?.[idx] || {};
    const firstName = instance.firstName || instance.first_name || instance.ownersFirstName || "";
    const lastName = instance.lastName || instance.last_name || instance.ownersLastName || "";
    if (firstName || lastName) return `${firstName} ${lastName}`.trim();
    return null;
  }, [docRef]);

  // ---- SUBMIT HANDLER ----

  const handleSubmitClick = useCallback(() => {
    setAllTouched(true);
    const { isValid, errors } = validateAll();
    setValidationErrors(errors);
    if (!isValid) {
      // Scroll to the TOP of the validation summary banner (its "שדות חובה חסרים"
      // title) so the user sees the start of the error list — via the shared helper
      // that scrolls only the real container and keeps the sticky headers visible.
      const focusBanner = (attemptsLeft) => {
        const banner = validationSummaryRef.current;
        if (banner) scrollElementIntoForm(banner);
        else if (attemptsLeft > 0) setTimeout(() => focusBanner(attemptsLeft - 1), 60);
      };
      setTimeout(() => focusBanner(6), 50);
      return;
    }
    onSendForm();
  }, [validateAll, onSendForm]);

  // ---- DUMMY DATA FILL ----

  const fillDummyData = useCallback(() => {
    const generateIsraeliId = () => {
      const digits = [];
      for (let i = 0; i < 8; i++) digits.push(Math.floor(Math.random() * 10));
      let sum = 0;
      for (let i = 0; i < 8; i++) {
        let val = digits[i] * ((i % 2) + 1);
        if (val > 9) val -= 9;
        sum += val;
      }
      digits.push((10 - (sum % 10)) % 10);
      return digits.join("");
    };

    const today = new Date().toISOString().split("T")[0];

    for (const tab of schema.tabs) {
      const tabId = tab.id;
      const isArrayTab = tab.isArray;
      const instances = isArrayTab ? (docRef.current[tabId] || [{}]) : [null];

      for (let idx = 0; idx < instances.length; idx++) {
        for (const section of tab.sections) {
          for (const field of section.fields) {
            if (field.type === "note" || field.type === "html") continue;

            // Skip hidden fields
            if (field.visible) {
              let visExpr = field.visible;
              if (isArrayTab) visExpr = visExpr.replaceAll("[x]", "[" + idx + "]");
              if (!evaluateVisibility(visExpr)) continue;
            }

            let val;
            switch (field.type) {
              case "text":
              case "id":
                val = field.validation?.includes("id") ? generateIsraeliId() : (field.caption || "טקסט לדוגמה");
                break;
              case "email":
                val = "test@example.com";
                break;
              case "phone":
                // normalized international form, e.g. "97250XXXXXXX"
                val = "9725" + String(Math.floor(10000000 + Math.random() * 89999999));
                break;
              case "number":
                val = Math.floor((field.min || 1) + Math.random() * ((field.max || 100) - (field.min || 1)));
                break;
              case "date":
                val = today;
                break;
              case "textarea":
                val = "טקסט ארוך לדוגמה לצורך בדיקה";
                break;
              case "select":
              case "radio":
              case "dropdown_db": {
                const opts = field.options || [];
                if (opts.length > 0) val = opts[Math.floor(Math.random() * opts.length)].value;
                break;
              }
              case "multiselect": {
                const opts = field.options || [];
                if (opts.length > 0) val = [opts[0].value];
                break;
              }
              case "checkbox":
                val = true;
                break;
              case "file":
                val = undefined;
                break;
              default:
                val = undefined;
            }

            if (val !== undefined) {
              if (isArrayTab) {
                if (!docRef.current[tabId]) docRef.current[tabId] = [{}];
                if (!docRef.current[tabId][idx]) docRef.current[tabId][idx] = {};
                docRef.current[tabId][idx][field.uid] = val;
              } else {
                if (!docRef.current[tabId] || typeof docRef.current[tabId] !== 'object') docRef.current[tabId] = {};
                docRef.current[tabId][field.uid] = val;
              }
            }
          }
        }
      }
    }
    updatePhones();
    updateState({ _dummyFillTrigger: Date.now() });
  }, [schema, docRef, updatePhones, updateState, evaluateVisibility]);

  // ---- PDF EXPORT ----

  const exportPDF = useCallback(() => {
    const form = document.querySelector(".tabs-layout");
    const root = document.getElementById("root");
    if (!form || !root) return;

    const originalParent = form.parentNode;
    const originalNext = form.nextSibling;

    // Move form out of the app, hide app
    root.style.display = "none";
    document.body.appendChild(form);

    const restore = () => {
      // Put form back where it was
      if (originalNext) {
        originalParent.insertBefore(form, originalNext);
      } else {
        originalParent.appendChild(form);
      }
      root.style.display = "";
    };

    window.addEventListener("afterprint", restore, { once: true });
    window.print();
    // Fallback restore in case afterprint doesn't fire
    setTimeout(restore, 3000);
  }, []);

  // ---- TAB NAVIGATION (mirrors original NextTab/PrevTab) ----

  const tabsBodyRef = useRef(null);
  const saveDropdownRef = useRef(null);
  const chevronBtnRef = useRef(null);
  const validationSummaryRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToTop = useCallback(() => {
    const sc = getScrollableAncestor(tabsBodyRef.current);
    if (sc) sc.scrollTo({ top: 0, behavior: "smooth" });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Scroll to top of the form AFTER React renders new tab content.
  // Scroll only the real container (not the window, not scrollIntoView) so the
  // sticky App Header and .tabs-header stay visible. rAF defers until layout settles.
  useEffect(() => {
    requestAnimationFrame(() => {
      const sc = getScrollableAncestor(tabsBodyRef.current);
      if (sc) sc.scrollTo({ top: 0, behavior: "auto" });
      else window.scrollTo(0, 0);
    });
  }, [step]);

  // Close save dropdown when clicking outside
  useEffect(() => {
    if (!saveDropdownOpen) { setDropdownPos(null); return; }
    const calcPos = () => {
      const el = chevronBtnRef.current || saveDropdownRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        setDropdownPos({ top: rect.bottom + 4, right: document.documentElement.clientWidth - rect.right });
      }
    };
    calcPos();
    window.addEventListener("resize", calcPos);
    window.addEventListener("scroll", calcPos, { passive: true });
    const handleClickOutside = (e) => {
      if (saveDropdownRef.current && !saveDropdownRef.current.contains(e.target)) {
        setSaveDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("resize", calcPos);
      window.removeEventListener("scroll", calcPos);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [saveDropdownOpen]);

  const goNextTab = useCallback(() => {
    if (executeFunctionByUid) executeFunctionByUid("NextTab");
    setStep((s) => s + 1);
    if (validateConnection) validateConnection();
    // Update owner names
    if (docRef.current?.owner) {
      const names = docRef.current.owner.map((item) =>
        item.firstName && item.lastName ? `${item.firstName} ${item.lastName}  ` : ""
      );
      updateState({ ownersNames: names });
    }
  }, [executeFunctionByUid, validateConnection, docRef, updateState]);

  const goPrevTab = useCallback(() => {
    setStep((s) => Math.max(0, s - 1));
  }, []);

  // ---- COMPUTED VALUES ----

  const visibleTabs = schema.tabs.filter((t) => {
    if (!t.visible) return true;
    if (t.isArray) {
      const items = docRef.current[t.id] || [{}];
      const expr = t.visible.replaceAll("[x]", "[" + (items.length - 1) + "]");
      return evaluateVisibility(expr);
    }
    return evaluateVisibility(t.visible);
  });

  const currentVisibleTab = visibleTabs[step] || visibleTabs[0];
  if (!currentVisibleTab) {
    return (
      <div className="tabs-layout">
        <div className="tabs-body" ref={tabsBodyRef}>
          <p>No visible tabs</p>
        </div>
      </div>
    );
  }

  const tab = currentVisibleTab;
  const tabId = tab.id;
  const isArrayTab = tab.isArray;
  const curIdx = instanceIndices[tabId] || 0;
  const instances = isArrayTab ? (docRef.current[tabId] || [{}]) : [];
  const safeCurIdx = isArrayTab ? Math.min(curIdx, Math.max(0, instances.length - 1)) : curIdx;

  const progressPct =
    visibleTabs.length > 0
      ? Math.round(((step + 1) / visibleTabs.length) * 100)
      : 0;

  return (
    <div
      className="tabs-layout"
      dir={state.dataFromDB?.RtlDir === false ? "ltr" : "rtl"}
      style={{ background: "var(--bg)", color: "var(--text)" }}
    >
      {/* Header */}
      <div className="tabs-header">
        <div className="tabs-title-row">
          <div>
            <h1>{schema.title || "Smart Forms"}</h1>
            {state.dataFromDB?.FormName && (
              <span className="tabs-sub">{state.dataFromDB.FormName}</span>
            )}
          </div>
          <div className="tabs-header-actions">
            <div className="tabs-progress">
              <span dir="ltr">
                שלב {step + 1} מתוך {visibleTabs.length}
              </span>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: progressPct + "%" }} />
              </div>
            </div>

            {/* Auto-save status + toggle — only once a draft exists */}
            {hasDraft && (
              <div className="autosave">
                <span className="autosave-status" aria-live="polite">
                  {state.isAutoSaving ? (
                    <span className="autosave-saving">שומר…</span>
                  ) : state.lastSavedAt ? (
                    <span className="autosave-saved">
                      <Icon name="check" size={12} /> נשמר {formatSavedTime(state.lastSavedAt)}
                    </span>
                  ) : null}
                </span>
                <button
                  className={`btn btn-ghost btn-sm autosave-toggle${state.autoSaveEnabled ? " on" : ""}`}
                  type="button"
                  onClick={toggleAutoSave}
                  title={state.autoSaveEnabled ? "שמירה אוטומטית פעילה — לחץ לכיבוי" : "שמירה אוטומטית כבויה — לחץ להפעלה"}
                >
                  <Icon name="save" size={14} />
                  <span className="desktop-only">{state.autoSaveEnabled ? "שמירה אוטו' פעילה" : "שמירה אוטו' כבויה"}</span>
                </button>
              </div>
            )}

            {/* Theme toggle */}
            <button
              className="btn btn-ghost btn-sm theme-toggle"
              type="button"
              onClick={toggleTheme}
              title={theme === "dark" ? "מצב בהיר" : "מצב כהה"}
            >
              {theme === "dark" ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                </svg>
              )}
            </button>

            {/* Upload file button */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,.txt,.xlsx"
              style={{ display: "none" }}
              onChange={(e) => {
                if (e.target.files[0]) handleUploadFile({ value: [e.target.files[0]] });
                e.target.value = "";
              }}
            />
            <button
              className="btn btn-ghost btn-sm"
              type="button"
              onClick={() => fileInputRef.current?.click()}
            >
              <Icon name="upload" size={14} /> טען קובץ
            </button>

            {/* Refresh button — opens centered confirm dialog */}
            <button
              className="btn btn-ghost btn-sm desktop-only"
              type="button"
              onClick={() => setRefreshPopupVisible(true)}
            >
              <Icon name="refresh" size={14} /> רענן
            </button>

            {/* Fill dummy data button — restricted to authorized emails */}
            {(state.user?.email || "").match(/ehcontrol\.com|hgr\.co\.il/) && (
              <button
                className="btn btn-ghost btn-sm desktop-only"
                type="button"
                onClick={fillDummyData}
                title="מלא נתוני דמה לצורך בדיקה"
              >
                <Icon name="wand" size={14} /> נתוני דמה
              </button>
            )}

            {/* Save dropdown */}
            <div ref={saveDropdownRef} style={{ position: "relative" }}>
              <div style={{ display: "flex" }}>
                <button
                  className={`btn btn-ghost btn-sm${saveHintVisible ? " hint-target" : ""}`}
                  type="button"
                  onClick={() => onSaveChanges("טיוטה")}
                >
                  <Icon name="save" size={14} /> שמור טיוטה
                </button>
                <button
                  ref={chevronBtnRef}
                  className="btn btn-ghost btn-sm"
                  type="button"
                  style={{ paddingLeft: 4, paddingRight: 4 }}
                  onClick={() => setSaveDropdownOpen((o) => !o)}
                >
                  <Icon name="chevronDown" size={14} />
                </button>
              </div>
              {/* One-off pointer shown right after the user mutes the prompt, so the
                  feature stays discoverable. Dismissed by the button, not a timer. */}
              {saveHintVisible && (
                <div className="save-hint" role="status">
                  <p>תמיד אפשר לשמור טיוטה מכאן, בכל שלב.</p>
                  <p className="save-hint-sub">
                    להחזרת ההודעה בעתיד — דרך העדפות בפרופיל.
                  </p>
                  <div className="save-hint-actions">
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => history.push("/profile")}
                    >
                      עבור להעדפות
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => setSaveHintVisible(false)}
                    >
                      הבנתי
                    </button>
                  </div>
                </div>
              )}
              {saveDropdownOpen && (
                <div
                  className="dropdown-menu"
                >
                  <button
                    className="dropdown-item"
                    type="button"
                    onClick={() => {
                      onSaveFile();
                      setSaveDropdownOpen(false);
                    }}
                  >
                    <Icon name="save" size={14} /> הורד קובץ
                  </button>
                  <button
                    className="dropdown-item"
                    type="button"
                    onClick={() => {
                      exportPDF();
                      setSaveDropdownOpen(false);
                    }}
                  >
                    <Icon name="pdf" size={14} /> הורד PDF
                  </button>
                  {state.sourceStatus !== "תבנית" && (
                    <button
                      className="dropdown-item"
                      type="button"
                      onClick={() => {
                        setSaveDropdownOpen(false);
                        setTemplateNamePopupVisible(true);
                      }}
                    >
                      <Icon name="doc" size={14} /> צור תבנית
                    </button>
                  )}
                  {state.sourceStatus === "תבנית" && (
                    <button
                      className="dropdown-item"
                      type="button"
                      onClick={() => {
                        onSaveChanges("תבנית");
                        setSaveDropdownOpen(false);
                      }}
                    >
                      <Icon name="save" size={14} /> עדכן תבנית
                    </button>
                  )}
                  {/* Mobile-only items (hidden on desktop via CSS) */}
                  <button
                    className="dropdown-item mobile-only"
                    type="button"
                    onClick={() => { setRefreshPopupVisible(true); setSaveDropdownOpen(false); }}
                  >
                    <Icon name="refresh" size={14} /> רענן
                  </button>
                  {(state.user?.email || "").match(/ehcontrol\.com|hgr\.co\.il/) && (
                    <button
                      className="dropdown-item mobile-only"
                      type="button"
                      onClick={() => { fillDummyData(); setSaveDropdownOpen(false); }}
                    >
                      <Icon name="wand" size={14} /> נתוני דמה
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Send button */}
            <button className="btn btn-primary btn-sm" type="button" onClick={handleSubmitClick}>
              <Icon name="send" size={14} /> שלח
            </button>
          </div>
        </div>
        <div className="tabs-nav">
          {visibleTabs.map((t, i) => (
            <button
              key={t.id}
              type="button"
              className={`tab-btn ${i === step ? "active" : ""}`}
              onClick={() => setStep(i)}
            >
              <Icon name={t.icon || "doc"} size={16} />
              <span>{t.title}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Body — stopPropagation on wheel prevents DX ScrollView from intercepting scroll */}
      <div className="tabs-body" ref={tabsBodyRef}>
        <form
          ref={formElementRef}
          id="frm"
          method="post"
          encType="multipart/form-data"
          action={REACT_APP_SERVER_URL + "docfile1"}
          onSubmit={(e) => e.preventDefault()}
        >
          {/* Tab header */}
          <div className="tab-header">
            <div>
              <div className="tab-eyebrow" dir="ltr">
                שלב {step + 1} מתוך {visibleTabs.length}
              </div>
              <h2>{tab.title}</h2>
            </div>
            <div className="tab-header-icon">
              <Icon name={tab.icon || "doc"} size={28} />
            </div>
          </div>

          {/* Array tab: instance tabs + add button in one row, below tab-header */}
          {isArrayTab && (
            <div className="instance-tabs">
              {instances.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  className={`inst-tab ${idx === safeCurIdx ? "active" : ""}`}
                  onClick={() => setCurrentInstance(tabId, idx)}
                >
                  <Icon name="user" size={14} />
                  <span>
                    {getInstanceLabel(tab, tabId, idx) ||
                      `${getInstanceBaseLabel(tab)} ${idx + 1}`}
                  </span>
                  {instances.length > 1 && (
                    <span
                      className="inst-remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeInstance(tabId, tab.title, idx);
                      }}
                    >
                      <Icon name="close" size={12} />
                    </span>
                  )}
                </button>
              ))}
              {(!tab.maxItems || instances.length < tab.maxItems) && (
                <button
                  type="button"
                  className="inst-add"
                  onClick={() => addInstance(tabId, tab.maxItems)}
                >
                  <Icon name="plus" size={14} />
                  <span>הוסף {getInstanceBaseLabel(tab)}</span>
                </button>
              )}
            </div>
          )}

          {/* Sections */}
          <div className="tab-content">
            {tab.sections.map((section, sIdx) => (
              <div key={sIdx} className={`form-section${section.isCard ? " question-card" : ""}`}>
                {section.title && (
                  <div className="section-head">
                    <h3>{section.title}</h3>
                  </div>
                )}
                {(() => {
                  // Helper to render a single field
                  const renderSingleField = (field) => {
                    let visExpr = field.visible;
                    if (visExpr && isArrayTab) {
                      visExpr = visExpr.replaceAll("[x]", "[" + safeCurIdx + "]");
                    }
                    const vis = evaluateVisibility(visExpr);
                    if (!vis) return null;

                    if (state.setDefaults && field.defaultValue && field.uid) {
                      if (isArrayTab) {
                        if (!docRef.current[tabId][safeCurIdx])
                          docRef.current[tabId][safeCurIdx] = {};
                        if (!Object.prototype.hasOwnProperty.call(docRef.current[tabId][safeCurIdx], field.uid)) {
                          docRef.current[tabId][safeCurIdx][field.uid] = field.defaultValue;
                        }
                      } else {
                        if (!docRef.current[tabId] || typeof docRef.current[tabId] !== 'object') docRef.current[tabId] = {};
                        if (docRef.current[tabId][field.uid] === undefined) {
                          docRef.current[tabId][field.uid] = field.defaultValue;
                        }
                      }
                    }

                    const value = isArrayTab
                      ? docRef.current[tabId]?.[safeCurIdx]?.[field.uid]
                      : docRef.current[tabId]?.[field.uid];

                    const fieldKey = isArrayTab
                      ? tabId + "[" + safeCurIdx + "]." + field.uid
                      : tabId + "." + field.uid;

                    const error = validateFormField(field, value, tabId, isArrayTab, safeCurIdx);
                    const showError = (touched[fieldKey] || allTouched) && error;

                    return renderField(
                      field, value,
                      (v) => handleFieldChange(tabId, isArrayTab, field, v, safeCurIdx),
                      () => handleBlur(fieldKey),
                      showError ? error : null,
                      state, tabId, isArrayTab, safeCurIdx, docRef, evaluateVisibility
                    );
                  };

                  // Group consecutive fields by cardGroup (supports nesting via "|")
                  const getLevel = (cg, level) => (cg || "").split("|")[level] || "";

                  // Level-0 chunking (first segment before |)
                  const chunks = [];
                  for (const field of section.fields) {
                    const l0 = getLevel(field.cardGroup, 0);
                    const last = chunks[chunks.length - 1];
                    if (last && last.group === l0) {
                      last.fields.push(field);
                    } else {
                      chunks.push({ group: l0, fields: [field] });
                    }
                  }

                  return chunks.map((chunk, cIdx) => {
                    if (!chunk.group) {
                      const rendered = chunk.fields.map(renderSingleField);
                      if (rendered.every((r) => r === null)) return null;
                      return (
                        <div key={"ug-" + cIdx} className="field-grid">
                          {rendered}
                        </div>
                      );
                    }

                    // Sub-chunk by level-1 (second segment after |)
                    const subChunks = [];
                    for (const field of chunk.fields) {
                      const l1 = getLevel(field.cardGroup, 1);
                      const last = subChunks[subChunks.length - 1];
                      if (last && last.group === l1) {
                        last.fields.push(field);
                      } else {
                        subChunks.push({ group: l1, fields: [field] });
                      }
                    }

                    // Check if all fields in this card are invisible
                    const allCardFields = chunk.fields.map(renderSingleField);
                    if (allCardFields.every((r) => r === null)) return null;

                    return (
                      <div key={"cg-" + cIdx} className="question-card-inline">
                        <h4 className="card-inline-title">{chunk.group}</h4>
                        {subChunks.map((sub, sIdx) => {
                          const subRendered = sub.fields.map(renderSingleField);
                          if (subRendered.every((r) => r === null)) return null;
                          if (!sub.group) {
                            return (
                              <div key={"sg-" + sIdx} className="field-grid">
                                {subRendered}
                              </div>
                            );
                          }
                          return (
                            <div key={"sn-" + sIdx} className="question-card-nested">
                              <h5 className="card-nested-title">{sub.group}</h5>
                              <div className="field-grid">
                                {subRendered}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  });
                })()}
              </div>
            ))}
          </div>

          {/* Array tab: second "add" button at the bottom of the tab content */}
          {isArrayTab && (!tab.maxItems || instances.length < tab.maxItems) && (
            <div className="instance-add-bottom">
              <button
                type="button"
                className="inst-add"
                onClick={() => {
                  const newIdx = instances.length;
                  addInstance(tabId, tab.maxItems);
                  setCurrentInstance(tabId, newIdx);
                  scrollToTop();
                }}
              >
                <Icon name="plus" size={14} />
                <span>הוסף {getInstanceBaseLabel(tab)}</span>
              </button>
            </div>
          )}

          {/* Navigation */}
          <nav className="wizard-nav">
            <button
              type="button"
              className="btn btn-ghost"
              disabled={step === 0}
              onClick={goPrevTab}
            >
              הקודם <Icon name="arrowRight" size={16} />
            </button>
            <span className="nav-hint">
              שלב {step + 1} מתוך {visibleTabs.length}
            </span>
            {step < visibleTabs.length - 1 ? (
              <button
                type="button"
                className="btn btn-primary"
                onClick={goNextTab}
              >
                <Icon name="arrowLeft" size={16} /> הבא
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-primary btn-send"
                onClick={handleSubmitClick}
              >
                <Icon name="send" size={16} /> שלח
              </button>
            )}
          </nav>
        </form>

        {/* Validation Summary Banner — inline below form, inside scroll container */}
        {validationErrors.length > 0 && (
          <div className="validation-summary" ref={validationSummaryRef}>
            <div className="validation-summary-header">
              <div className="validation-summary-title">
                <Icon name="alert" size={16} />
                <span>שדות חובה חסרים ({validationErrors.length})</span>
              </div>
              <button
                type="button"
                className="validation-summary-close"
                onClick={() => setValidationErrors([])}
                title="סגור"
              >
                <Icon name="close" size={14} />
              </button>
            </div>
            <ul className="validation-summary-list">
              {validationErrors.map((err, i) => (
                <li key={i}>
                  <button
                    type="button"
                    className="validation-summary-item"
                    onClick={() => {
                      setStep(err.tabIndex);
                      if (err.tabId && err.instanceIdx !== undefined) {
                        setCurrentInstance(err.tabId, err.instanceIdx);
                      }
                      const tryFocus = (attemptsLeft) => {
                        const el = document.querySelector(
                          `[data-field-uid="${err.fieldUid}"]`
                        );
                        if (el) {
                          scrollElementIntoForm(el);
                          const focusable = el.querySelector("input, select, textarea, button");
                          // preventScroll: focus() would otherwise re-scroll (via every
                          // ancestor) and tuck the sticky headers out of view again.
                          if (focusable) focusable.focus({ preventScroll: true });
                        } else if (attemptsLeft > 0) {
                          setTimeout(() => tryFocus(attemptsLeft - 1), 80);
                        }
                      };
                      setTimeout(() => tryFocus(6), 80);
                    }}
                  >
                    <Icon name="info" size={12} />
                    <span>{err.message}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Auto-save opt-in prompt on a not-yet-saved form.
          Rendered through a portal: the app scrolls an inner DevExtreme container whose
          ancestors are overflow:hidden, which clips a position:fixed overlay to that box
          instead of the viewport. Mounting on <body> lets it cover the whole screen.
          The overlay has no click-to-close: dismissing also silences the offer for the
          rest of the session, so it has to be a deliberate choice. Escape still closes. */}
      {autoSavePromptVisible && !muteConfirmVisible && createPortal(
        <div className="modal-overlay">
          <div
            className="modal-content"
            ref={autoSaveModalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="autosave-modal-title"
          >
            <div className="modal-title" id="autosave-modal-title">שמירת טיוטה</div>
            <p className="modal-body-text">
              האם לשמור טיוטה ולהפעיל שמירה אוטומטית שקטה מכאן והלאה?
            </p>
            {autoSaveError && (
              <div className="field-warning">
                <Icon name="alert" size={16} />
                <span>{autoSaveError}</span>
              </div>
            )}
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={dismissAutoSavePrompt}
                disabled={autoSaveSubmitting}
              >
                לא עכשיו
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                ref={autoSaveConfirmRef}
                onClick={confirmAutoSavePrompt}
                disabled={autoSaveSubmitting}
              >
                <Icon name="save" size={14} />
                {autoSaveSubmitting ? "שומר…" : autoSaveError ? "נסה שוב" : "שמור והפעל"}
              </button>
            </div>
            {/* Only the box toggles it — clicking the wording next to it must not,
                since ticking immediately opens the confirmation. */}
            <div className="modal-checkbox checkbox-row">
              <label className="checkbox-hit">
                <input
                  type="checkbox"
                  checked={autoSavePromptDontAsk}
                  onChange={(e) => {
                    setAutoSavePromptDontAsk(e.target.checked);
                    // Ticking is the decision point: confirm right away rather than
                    // waiting for the user to also pick one of the buttons.
                    if (e.target.checked) setMuteConfirmVisible(true);
                  }}
                  disabled={autoSaveSubmitting}
                />
                <span className="checkbox-box"><Icon name="check" size={11} /></span>
              </label>
              <span>אל תציג הודעה זו שוב</span>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Second confirmation for "don't show again" — see confirmMute. */}
      {muteConfirmVisible && createPortal(
        <div className="modal-overlay">
          <div
            className="modal-content"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mute-confirm-title"
          >
            <div className="modal-title" id="mute-confirm-title">
              בטוח שברצונך לכבות את ההצעה לשמירת טיוטה?
            </div>
            <p className="modal-body-text">
              💡 חשוב לדעת: השמירה האוטומטית מגנה על מה שמילאת — אם הדפדפן ייסגר
              או שתצטרך לעצור באמצע, הנתונים יישמרו ולא יימחקו.
            </p>
            <div className="modal-actions">
              <button type="button" className="btn btn-ghost btn-sm" onClick={cancelMute}>
                ביטול
              </button>
              <button type="button" className="btn btn-primary btn-sm" onClick={confirmMute}>
                כן, אל תציג שוב
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Refresh confirm dialog */}
      {refreshPopupVisible && (
        <div className="modal-overlay" onClick={() => setRefreshPopupVisible(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">יציאה</div>
            <p className="modal-body-text">יציאה מההסכם לא תשמור את הנתונים!</p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setRefreshPopupVisible(false)}
              >
                סגור
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => {
                  setRefreshPopupVisible(false);
                  deleteAndReset();
                }}
              >
                <Icon name="refresh" size={14} /> מחק ורענן
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Name Popup */}
      {templateNamePopupVisible && (
        <div className="modal-overlay" onClick={() => setTemplateNamePopupVisible(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">צור תבנית</div>
            <div className="field">
              <label className="field-label">שם תבנית</label>
              <input
                type="text"
                value={state.draftName || ""}
                onChange={(e) => updateState({ draftName: e.target.value })}
                placeholder="הזן שם לתבנית"
              />
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setTemplateNamePopupVisible(false)}
              >
                ביטול
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => {
                  setTemplateNamePopupVisible(false);
                  onSaveChanges("תבנית");
                }}
              >
                שמור תבנית
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============ Field Renderer ============ */

/**
 * Apply a mask to a raw value string.
 * Mask chars: 0 = required digit, others = literals.
 *
 * IMPORTANT: masks containing '#' (e.g. "0.#####") are DevExtreme number-format strings
 * used for display purposes only (like printf formats). They are NOT digit-entry masks
 * and must be ignored here — test-form.js passes them as `format` to dxNumberBox/dxSlider,
 * not as `mask` to dxTextBox. Applying them as digit masks corrupts numeric input.
 *
 * Only plain digit masks (e.g. "052-0000000", "(0##) 000-0000") should go through this function.
 */
function applyMask(rawValue, mask) {
  if (!mask) return rawValue;
  const maskStr = String(mask);
  // If mask contains '#', it is a DevExtreme format string — leave value untouched
  if (maskStr.includes("#")) return rawValue;
  const digits = String(rawValue).replace(/[^\d]/g, "");
  let result = "";
  let dIdx = 0;
  for (let i = 0; i < maskStr.length && dIdx <= digits.length; i++) {
    const m = maskStr[i];
    if (m === "0") {
      if (dIdx < digits.length) {
        result += digits[dIdx++];
      } else {
        break; // required digit missing, stop
      }
    } else {
      // literal char — insert only if there are more digits to come
      if (dIdx < digits.length) {
        result += m;
      }
    }
  }
  return result;
}

/**
 * A DevExtreme number "mask" containing '#' (e.g. "###,###,###", "0.##", "#,##0.00")
 * is a DISPLAY FORMAT string, not a digit-entry mask. For number fields it must only
 * affect how the value is shown — never the stored value.
 */
function isFormatMask(mask) {
  return !!mask && String(mask).includes("#");
}

/**
 * Format a clean numeric value for DISPLAY using a DevExtreme-style format mask.
 * The stored value stays a clean numeric string; this only affects presentation.
 * Derives thousands grouping (mask contains ",") and decimal places from the mask's
 * fractional part ('0' = required digit, '#' = optional digit).
 */
function formatNumberForDisplay(value, mask) {
  if (value === "" || value === null || value === undefined) return "";
  const str = String(value).replace(/,/g, "");
  if (str === "-" || str === "." || str === "-.") return str;
  const num = Number(str);
  if (!isFinite(num)) return String(value);

  const maskStr = String(mask);
  const useGrouping = maskStr.includes(",");
  const dotIdx = maskStr.indexOf(".");
  let minFrac = 0;
  let maxFrac = 0;
  if (dotIdx !== -1) {
    for (const ch of maskStr.slice(dotIdx + 1)) {
      if (ch === "0") { minFrac++; maxFrac++; }
      else if (ch === "#") { maxFrac++; }
    }
  }
  try {
    return new Intl.NumberFormat("en-US", {
      useGrouping,
      minimumFractionDigits: minFrac,
      maximumFractionDigits: Math.max(maxFrac, minFrac),
    }).format(num);
  } catch {
    return String(value);
  }
}

/** Strip to a clean numeric string: digits, a single leading '-', a single '.'. */
function cleanNumeric(str) {
  let s = String(str == null ? "" : str);
  const neg = s.trim().startsWith("-");
  s = s.replace(/[^\d.]/g, "");
  const firstDot = s.indexOf(".");
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "");
  }
  return (neg ? "-" : "") + s;
}

/** Add thousands separators to the integer part of a clean numeric string (keeps a partial ".", "-"). */
function groupNumeric(clean) {
  if (clean === "" || clean === "-") return clean;
  const neg = clean.startsWith("-");
  const body = neg ? clean.slice(1) : clean;
  const dotIdx = body.indexOf(".");
  let intPart = dotIdx === -1 ? body : body.slice(0, dotIdx);
  const decPart = dotIdx === -1 ? "" : body.slice(dotIdx);
  intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return (neg ? "-" : "") + intPart + decPart;
}

/** Caret index in `str` that has `nonCommaCount` non-comma chars to its left. */
function caretForNonComma(str, nonCommaCount) {
  if (nonCommaCount <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < str.length; i++) {
    if (str[i] !== ",") seen++;
    if (seen === nonCommaCount) return i + 1;
  }
  return str.length;
}

/**
 * Number field for a DevExtreme format mask (e.g. "###,###,###"). Groups thousands
 * LIVE while typing (so the user sees commas as they type) but stores a clean numeric
 * string in docRef. Caret position is preserved across reformatting.
 */
function FormatNumberField({ field, value, onChange, onBlur, isRequired, helpText }) {
  const mask = field.mask;
  const inputRef = useRef(null);
  const caretRef = useRef(null);
  const [display, setDisplay] = useState(() => groupNumeric(cleanNumeric(value ?? "")));

  // Re-sync when the stored value changes externally (draft reload, dummy fill, defaults).
  useEffect(() => {
    if (cleanNumeric(value ?? "") !== cleanNumeric(display)) {
      setDisplay(groupNumeric(cleanNumeric(value ?? "")));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Restore the caret after a live reformat inserted/removed commas.
  useLayoutEffect(() => {
    if (caretRef.current == null || !inputRef.current) return;
    const pos = caretForNonComma(display, caretRef.current);
    try { inputRef.current.setSelectionRange(pos, pos); } catch (e) { /* ignore */ }
    caretRef.current = null;
  }, [display]);

  const handleChange = (e) => {
    const input = e.target;
    const raw = input.value;
    const caret = input.selectionStart == null ? raw.length : input.selectionStart;
    caretRef.current = raw.slice(0, caret).replace(/,/g, "").length;
    const clean = cleanNumeric(raw);
    setDisplay(groupNumeric(clean));
    onChange(clean);
  };

  return (
    <>
      <label className="field-label">
        {field.caption}
        {isRequired && <span className="field-req">*</span>}
      </label>
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        name={field.uid}
        value={display}
        onChange={handleChange}
        onBlur={(e) => { setDisplay(formatNumberForDisplay(cleanNumeric(display), mask)); if (onBlur) onBlur(e); }}
        min={field.min ?? undefined}
        max={field.max ?? undefined}
        placeholder={field.placeholder || ""}
      />
      {helpText}
    </>
  );
}

/** Number field without a display format mask (plain number or a digit-entry mask). */
function PlainNumberField({ field, value, onChange, onBlur, isRequired, helpText }) {
  const mask = field.mask;
  const digitMask = !!mask; // format masks are handled by FormatNumberField
  const asText = !!mask || field.validation?.includes("numeric");
  const display = digitMask ? applyMask(value ?? "", mask) : (value ?? "");

  return (
    <>
      <label className="field-label">
        {field.caption}
        {isRequired && <span className="field-req">*</span>}
      </label>
      <input
        type={asText ? "text" : "number"}
        name={field.uid}
        value={display}
        onChange={(e) => {
          const raw = e.target.value;
          if (digitMask) onChange(applyMask(raw, mask));
          else onChange(raw ? Number(raw) : "");
        }}
        onBlur={onBlur}
        min={field.min ?? undefined}
        max={field.max ?? undefined}
        placeholder={field.placeholder || (digitMask ? String(mask) : "")}
      />
      {helpText}
    </>
  );
}

/**
 * Number input. Mask affects DISPLAY only — the value stored in docRef is always a
 * clean numeric string (for format masks) / Number (no mask). min/max are independent
 * of the mask (range enforcement lives in validateFormField).
 */
function NumberField(props) {
  return isFormatMask(props.field.mask)
    ? <FormatNumberField {...props} />
    : <PlainNumberField {...props} />;
}

/**
 * Country flag as an image. Emoji flags don't render on Windows (they show the ISO
 * letters), so we use flag images (flagcdn) and fall back to the ISO code via `alt`
 * if the image can't load.
 */
function CountryFlag({ iso }) {
  const code = String(iso).toLowerCase();
  return (
    <img
      className="phone-cc-flag"
      src={`https://flagcdn.com/24x18/${code}.png`}
      srcSet={`https://flagcdn.com/48x36/${code}.png 2x`}
      width="24"
      height="18"
      alt={iso}
      loading="lazy"
    />
  );
}

/**
 * Phone input with a country-code selector (default Israel).
 * The user edits the local number ("0541234567"); the value stored in docRef is
 * the normalized international form ("972541234567"). Local input text is kept in
 * component state so typing (incl. leading zeros) is natural, while an effect
 * re-syncs when the stored value changes externally (draft reload, dummy fill).
 */
function PhoneField({ field, value, onChange, onBlur, isRequired, helpText }) {
  const init = parsePhone(value, DEFAULT_COUNTRY_ISO);
  const [iso, setIso] = useState(init.iso);
  const [local, setLocal] = useState(init.local);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const ccRef = useRef(null);    // whole .phone-input — anchors the menu
  const ccBtnRef = useRef(null); // the trigger alone — identifies a click on it

  const country =
    COUNTRY_CODES.find((c) => c.iso === iso) ||
    COUNTRY_CODES.find((c) => c.iso === DEFAULT_COUNTRY_ISO);

  // Re-sync when the stored value changes externally and no longer matches our input.
  useEffect(() => {
    const emitted = normalizePhone(local, country && country.dialCode);
    if ((value || "") !== emitted) {
      const p = parsePhone(value, DEFAULT_COUNTRY_ISO);
      setIso(p.iso);
      setLocal(p.local);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const menuRef = useRef(null);
  const [menuPos, setMenuPos] = useState(null);

  // Close the country menu on outside click / Escape.
  // Two things make this fiddly, and both caused the menu to appear not to open:
  //  - mousedown fires before click, so closing on mousedown let the button's own
  //    onClick reopen it immediately. Hence click.
  //  - the click that opened the menu is still propagating to document when this
  //    effect runs, and the menu isn't rendered yet (menuPos is set in a layout
  //    effect), so the guards below would miss and close it again. Registering on
  //    the next task lets that click finish first.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      const inTrigger = ccBtnRef.current && ccBtnRef.current.contains(e.target);
      const inMenu = menuRef.current && menuRef.current.contains(e.target);
      if (!inTrigger && !inMenu) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const t = setTimeout(() => document.addEventListener("click", onDocClick), 0);
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // The menu is portalled to <body> and positioned as fixed, so it is bounded by the
  // viewport rather than by the trigger's scroll container. It is always clamped to fit
  // on screen: preferring below the trigger, flipping above when that side is roomier,
  // and finally pinning to the viewport edge when neither side alone is enough. That
  // last case is why no scrolling is needed to reveal it.
  useLayoutEffect(() => {
    if (!open) { setMenuPos(null); return; }
    const place = () => {
      const el = ccRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const vh = document.documentElement.clientHeight;
      const GAP = 4;
      const EDGE = 8;
      const below = vh - r.bottom - GAP - EDGE;
      const above = r.top - GAP - EDGE;
      const flip = below < MENU_MIN_H && above > below;
      // Height the chosen side allows, but never so small it is unusable — the top
      // clamp below then slides the menu off the trigger to make that height fit.
      const room = Math.max(flip ? above : below, 0);
      const usable = Math.max(0, vh - 2 * EDGE);
      const height = Math.min(MENU_MAX_H, usable, Math.max(room, Math.min(MENU_MIN_H, usable)));
      // Always resolve to a top, then clamp it into [EDGE, vh - height - EDGE]. Doing
      // the arithmetic in one coordinate space avoids mixing top- and bottom-anchored
      // maths, which previously pushed the menu off screen near the bottom of the page.
      const wanted = flip ? r.top - GAP - height : r.bottom + GAP;
      const top = Math.max(EDGE, Math.min(wanted, vh - height - EDGE));
      setMenuPos({ top, left: r.left, maxHeight: height });
    };
    place();

    // Scrolling the form closes the menu rather than dragging it along: a fixed menu
    // chasing the trigger down the page reads as broken. Scrolling the country list
    // inside the menu is ignored.
    const onScroll = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener("resize", place);
    window.addEventListener("scroll", onScroll, { passive: true, capture: true });
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", onScroll, { capture: true });
    };
  }, [open]);

  // Mouse wheel over the country list: the DevExtreme ScrollView that wraps the page
  // swallows the wheel event, so the list never scrolls on its own. Scroll it directly
  // and stop the event from reaching DX. Registered natively (not via onWheel) because
  // React's wheel listener is passive and cannot call preventDefault.
  const listRef = useRef(null);
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const onWheel = (e) => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      // Let the page scroll take over once the list hits an edge.
      const atTop = scrollTop <= 0 && e.deltaY < 0;
      const atBottom = scrollTop + clientHeight >= scrollHeight - 1 && e.deltaY > 0;
      if (atTop || atBottom) return;
      e.preventDefault();
      e.stopPropagation();
      el.scrollTop += e.deltaY;
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
    // menuPos too: the list only exists once it is set, so on [open] alone listRef is
    // still null here and the handler was never attached.
  }, [open, menuPos]);

  const handleLocalChange = (raw) => {
    setLocal(raw);
    onChange(normalizePhone(raw, country && country.dialCode));
  };

  const selectCountry = (nextIso) => {
    setIso(nextIso);
    setOpen(false);
    setFilter("");
    const c = COUNTRY_CODES.find((x) => x.iso === nextIso);
    onChange(normalizePhone(local, c && c.dialCode));
  };

  const q = filter.trim().toLowerCase();
  const filtered = q
    ? COUNTRY_CODES.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.iso.toLowerCase().includes(q) ||
          c.dialCode.includes(q.replace(/^\+/, ""))
      )
    : COUNTRY_CODES;

  return (
    <>
      <label className="field-label">
        {field.caption}
        {isRequired && <span className="field-req">*</span>}
      </label>
      <div className="phone-input" dir="ltr" ref={ccRef}>
        <button
          type="button"
          className="phone-cc-btn"
          ref={ccBtnRef}
          onClick={() => setOpen((o) => !o)}
          aria-label="קידומת מדינה"
          aria-expanded={open}
        >
          {country && <CountryFlag iso={country.iso} />}
          <span className="phone-cc-dial">+{country && country.dialCode}</span>
          <Icon name="chevronDown" size={14} />
        </button>
        <input
          type="tel"
          className="phone-num"
          name={field.uid}
          value={local}
          onChange={(e) => handleLocalChange(e.target.value)}
          onBlur={onBlur}
          placeholder={field.placeholder || "0541234567"}
        />
        {open && menuPos && createPortal(
          <div
            className="phone-cc-menu phone-cc-menu-floating"
            ref={menuRef}
            dir="ltr"
            style={{
              top: menuPos.top,
              left: menuPos.left,
              "--cc-max-h": `${menuPos.maxHeight}px`,
            }}
          >
            <input
              type="text"
              className="phone-cc-search"
              placeholder="חיפוש מדינה…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              autoFocus
            />
            <ul className="phone-cc-list" ref={listRef}>
              {filtered.map((c) => (
                <li key={c.iso}>
                  <button
                    type="button"
                    className={`phone-cc-item${c.iso === iso ? " active" : ""}`}
                    onClick={() => selectCountry(c.iso)}
                  >
                    <CountryFlag iso={c.iso} />
                    <span className="phone-cc-name">{c.name}</span>
                    <span className="phone-cc-dial">+{c.dialCode}</span>
                  </button>
                </li>
              ))}
              {filtered.length === 0 && (
                <li className="phone-cc-empty">לא נמצאה מדינה</li>
              )}
            </ul>
          </div>,
          document.body
        )}
      </div>
      {helpText}
    </>
  );
}

function renderField(field, value, onChange, onBlur, error, state, tabId, isArrayTab, curIdx, docRef, evaluateVisibility) {
  const colClass = `field-cell col-${field.col || 2}`;
  const fieldKey = isArrayTab
    ? tabId + "[" + curIdx + "]." + field.uid
    : tabId + "." + field.uid;

  // Resolve required: supports conditional expressions (requiredExpr) and boolean field.required
  const isRequired = field.requiredExpr
    ? !!evaluateVisibility(isArrayTab ? field.requiredExpr.replaceAll("[x]", "[" + curIdx + "]") : field.requiredExpr)
    : !!field.required;

  // Compute file input name for FormData
  const fileInputName = isArrayTab
    ? "fileUploader" + field.uid
    : "fileUploader" + field.uid;

  const helpTextEl = field.helpText ? (
    <div className="field-help">{field.helpText}</div>
  ) : null;

  const wrapField = (inner) => (
    <div className={colClass} key={fieldKey} data-field-uid={field.uid} dir={field.direction || undefined}>
      <div className={`field${error ? " has-error" : ""}`}>
        {inner}
        {error && (
          <div className="field-error">
            <Icon name="info" size={12} />
            <span>{error}</span>
          </div>
        )}
      </div>
    </div>
  );

  switch (field.type) {
    case "phone":
      return wrapField(
        <PhoneField
          field={field}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          isRequired={isRequired}
          helpText={helpTextEl}
        />
      );

    case "text":
    case "email":
    case "id":
      return wrapField(
        <>
          <label className="field-label">
            {field.caption}
            {isRequired && <span className="field-req">*</span>}
          </label>
          <input
            type={field.type === "email" ? "email" : "text"}
            name={field.uid}
            value={field.mask ? applyMask(value || "", field.mask) : (value || "")}
            onChange={(e) => {
              if (field.mask) {
                const masked = applyMask(e.target.value, field.mask);
                onChange(masked);
              } else {
                onChange(e.target.value);
              }
            }}
            onBlur={onBlur}
            placeholder={field.placeholder || (field.mask && !String(field.mask).includes("#") ? String(field.mask) : "")}
          />
          {helpTextEl}
        </>
      );

    case "number":
      return wrapField(
        <NumberField
          field={field}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          isRequired={isRequired}
          helpText={helpTextEl}
        />
      );

    case "date":
      return wrapField(
        <>
          <label className="field-label">
            {field.caption}
            {isRequired && <span className="field-req">*</span>}
          </label>
          <DateBox
            key={fieldKey}
            value={value || null}
            onValueChanged={(e) => onChange(e.value ?? "")}
            onFocusOut={onBlur}
            displayFormat="dd/MM/yyyy"
            dateSerializationFormat="yyyy-MM-dd"
            useMaskBehavior={true}
            rtlEnabled={true}
            type="date"
            pickerType="calendar"
            showClearButton={false}
            min={field.min || undefined}
            max={field.max || undefined}
            dateOutOfRangeMessage={field.validationMessage || "תאריך מחוץ לטווח המותר"}
          />
          {helpTextEl}
        </>
      );

    case "textarea":
      return wrapField(
        <>
          <label className="field-label">
            {field.caption}
            {isRequired && <span className="field-req">*</span>}
          </label>
          <textarea
            name={field.uid}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            rows={4}
            placeholder={field.placeholder || ""}
            disabled={field.disabledByRep && evaluateVisibility ? evaluateVisibility(field.disabledByRep) : false}
          />
          {helpTextEl}
        </>
      );

    case "select":
      return wrapField(
        <>
          <label className="field-label">
            {field.caption}
            {isRequired && <span className="field-req">*</span>}
          </label>
          <div className="select-wrap">
            <Icon name="chevronDown" size={16} />
            <select
              name={field.uid}
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              onBlur={onBlur}
              tabIndex={0}
              style={{ position: "relative", zIndex: 1 }}
            >
              <option value="">לחץ לבחירה</option>
              {field.isRoleSelect && state?.agentArrWithId
                ? state.agentArrWithId.map((agent) => (
                    <option key={agent.Aid} value={agent.Aid}>
                      {agent.Name}
                    </option>
                  ))
                : (field.options || []).map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
            </select>
          </div>
          {helpTextEl}
        </>
      );

    case "multiselect":
      return wrapField(
        <>
          <label className="field-label">
            {field.caption}
            {isRequired && <span className="field-req">*</span>}
          </label>
          <div className="chip-group">
            {(field.options || []).map((opt) => {
              const selected = Array.isArray(value) && value.includes(opt.value);
              return (
                <button
                  type="button"
                  key={opt.value}
                  className={`chip ${selected ? "selected" : ""}`}
                  onClick={() => {
                    const cur = Array.isArray(value) ? [...value] : [];
                    if (selected) {
                      onChange(cur.filter((v) => v !== opt.value));
                    } else {
                      onChange([...cur, opt.value]);
                    }
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          {helpTextEl}
        </>
      );

    case "radio":
      return wrapField(
        <>
          <label className="field-label">
            {field.caption}
            {isRequired && <span className="field-req">*</span>}
          </label>
          <div className={`radio-group${field.radioLayout === "inline" ? " inline" : field.radioLayout === "stacked" ? " stacked" : (field.options || []).length <= 3 ? " cols-inline" : ""}`}>
            {(field.options || []).map((opt) => (
              <label
                key={opt.value}
                className={`radio-pill ${value === opt.value ? "selected" : ""}`}
              >
                <input
                  type="radio"
                  name={field.uid}
                  value={opt.value}
                  checked={value === opt.value}
                  onChange={() => onChange(opt.value)}
                  onBlur={onBlur}
                />
                <span className="radio-dot" />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
          {helpTextEl}
        </>
      );

    case "checkbox":
      return (
        <div className={colClass} key={field.uid} data-field-uid={field.uid}>
          <div className={`field${error ? " has-error" : ""}`}>
            <label className="checkbox-row">
              <input type="checkbox" checked={!!value} onChange={() => onChange(!value)} onBlur={onBlur} />
              <span className="checkbox-box">
                <Icon name="check" size={12} />
              </span>
              <span>{field.caption}</span>
              {isRequired && <span className="field-req">*</span>}
            </label>
            {error && (
              <div className="field-error">
                <Icon name="info" size={12} />
                <span>{error}</span>
              </div>
            )}
          </div>
        </div>
      );

    case "file":
      return (
        <div className={colClass} key={field.uid} data-field-uid={field.uid}>
          <div className={`field${error ? " has-error" : ""}`}>
            <label className="field-label">
              {field.caption}
              {isRequired && <span className="field-req">*</span>}
            </label>
            <label className="file-drop">
              <input
                type="file"
                name={"fileUploader" + field.uid}
                multiple={!!field.multiple}
                accept={field.multiple ? ".pdf,.docx" : "image/*,application/pdf"}
                onChange={(e) => {
                  const files = e.target.files;
                  if (files && files.length > 0) {
                    onChange(field.multiple ? Array.from(files) : files[0]);
                  }
                }}
                onBlur={onBlur}
              />
              {(() => {
                const hasFile = value instanceof File ||
                  (Array.isArray(value) && value.length > 0 && value[0] instanceof File);
                return (
                  <div className={`file-drop-inner ${hasFile ? "has-file" : ""}`}>
                    <Icon name={hasFile ? "check" : "upload"} size={22} />
                    <div className="file-info">
                      {hasFile ? (
                        Array.isArray(value) ? (
                          <>
                            <strong>{value.length} קבצים נבחרו</strong>
                            <ul className="file-names-list">
                              {value.map((f, i) => (
                                <li key={i}>
                                  <span className="file-name">{f.name}</span>
                                  <span className="file-size">{Math.round(f.size / 1024)} kb</span>
                                </li>
                              ))}
                            </ul>
                            <span>לחץ להחלפה</span>
                          </>
                        ) : (
                          <>
                            <strong>{value.name}</strong>
                            <span className="file-size">{Math.round(value.size / 1024)} kb</span>
                            <span>לחץ להחלפה</span>
                          </>
                        )
                      ) : (
                        <>
                          <strong>העלה קובץ</strong>
                          <span>לחץ או גרור קובץ לכאן</span>
                        </>
                      )}
                    </div>
                  </div>
                );
              })()}
            </label>
            {helpTextEl}
            {error && (
              <div className="field-error">
                <Icon name="info" size={12} />
                <span>{error}</span>
              </div>
            )}
          </div>
        </div>
      );

    case "html":
      return (
        <div className="field-cell col-1" key={field.uid}>
          <div
            className="field-html"
            dangerouslySetInnerHTML={{
              __html: field.htmlContent || (field.caption || "").replace(/\n/g, "<br>"),
            }}
          />
        </div>
      );

    case "note": {
      const isWarning = (field.validationPatameters || "").toUpperCase() === "WARNING";
      return (
        <div className="field-cell col-1" key={field.uid}>
          {isWarning ? (
            <div className="field-warning">
              <Icon name="alert" size={16} />
              <span
                dangerouslySetInnerHTML={{
                  __html: (field.noteText || field.caption || "").replace(/\n/g, "<br>"),
                }}
              />
            </div>
          ) : (
            <div
              className="field-html"
              dangerouslySetInnerHTML={{
                __html: (field.noteText || field.caption || "").replace(/\n/g, "<br>"),
              }}
            />
          )}
        </div>
      );
    }

    case "dropdown_db": {
      // Get data from DataSet
      const formData = docRef?.current || {};
      let dropdownData = DataSetManager.getData(field.dataSetName);

      // Apply filter if specified
      if (field.filter && dropdownData.length > 0) {
        // Check if parent fields have values (for HideIfParentEmpty)
        if (field.hideIfParentEmpty && !FilterEngine.hasParentValues(field.filter, formData)) {
          return null; // Hide field if parent is empty
        }
        // Apply the filter
        dropdownData = FilterEngine.filterData(dropdownData, field.filter, formData);
      }

      // Apply Distinct - show only unique values
      if (field.distinct && dropdownData.length > 0) {
        const seen = new Set();
        const valueKey = field.valueField || "KEY";
        dropdownData = dropdownData.filter(item => {
          const val = item[valueKey];
          if (seen.has(val)) return false;
          seen.add(val);
          return true;
        });
      }

      // Build options from filtered data
      const options = dropdownData.map(item => ({
        value: item[field.valueField || "KEY"],
        label: item[field.displayField || "Caption"],
      }));

      return wrapField(
        <>
          <label className="field-label">
            {field.caption}
            {isRequired && <span className="field-req">*</span>}
          </label>
          <div className="select-wrap">
            <Icon name="chevronDown" size={16} />
            <select
              name={field.uid}
              value={value || ""}
              onChange={(e) => {
                onChange(e.target.value);
                // Handle ResetFields - clear child fields when parent changes
                if (field.resetFields && Array.isArray(field.resetFields)) {
                  field.resetFields.forEach((fieldUid) => {
                    if (isArrayTab && formData[tabId]?.[curIdx]) {
                      formData[tabId][curIdx][fieldUid] = null;
                    } else if (formData[tabId]) {
                      formData[tabId][fieldUid] = null;
                    }
                  });
                }
              }}
              onBlur={onBlur}
              tabIndex={0}
              style={{ position: "relative", zIndex: 1 }}
            >
              <option value="">לחץ לבחירה</option>
              {options.map((opt, idx) => (
                <option key={`${opt.value}_${idx}`} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          {helpTextEl}
        </>
      );
    }

    default:
      return wrapField(
        <>
          <label className="field-label">
            {field.caption}
            {isRequired && <span className="field-req">*</span>}
          </label>
          <input
            type="text"
            name={field.uid}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            placeholder={field.placeholder || ""}
          />
          {helpTextEl}
        </>
      );
  }
}
